import React, {useEffect, useState} from "react";
import axios from "axios";
import Swal from "sweetalert2";
import ERPDataGrid from "../ERPDataGrid";
import "../ERPTheme.css";
import {baseUrl, ipAddress, userName} from "../Univ";
function PurchaseOrder() {
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [supplierList, setSupplierList] = useState([]);
    const [quotationList, setQuotationList] = useState([]);
    const [inventoryItems, setInventoryItems] = useState([]);
    const [editId, setEditId] = useState(null);
    const [orderItems, setOrderItems] = useState([{
        item: "", quantity: "", rate: "", total: ""
    }]);
    const [formData, setFormData] = useState({
        po_no: "",
        supplier: "",
        quotation: "",
        order_date: "",
        expected_date: "",
        total: "",
        status: "OPEN",
        remarks: ""
    });
    useEffect(() => {
        getPurchaseOrders();
        getSuppliers();
        getQuotations();
        getInventoryItems();
    }, []);
    const getPurchaseOrders = () => {
        setLoading(true);
        axios.get(baseUrl + "purchase-order/")
            .then((res) => {
                setRows(res.data);
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
            });
    };
    const getSuppliers = () => {
        axios.get(baseUrl + "user-profile/")
            .then((res) => {
                setSupplierList(res.data);
            });
    };
    const getQuotations = () => {
        axios.get(baseUrl + "supplier-quotation/")
            .then((res) => {
                setQuotationList(res.data);
            });
    };
    const getInventoryItems = () => {
        axios.get(baseUrl + "inventory-item/")
            .then((res) => {
                setInventoryItems(res.data);
            });
    };
    const handleChange = (e) => {
        setFormData({
            ...formData, [e.target.name]: e.target.value
        });
    };
    const handleItemChange = (index, e) => {
        const values = [...orderItems];
        values[index][e.target.name] = e.target.value;
        if (values[index].quantity && values[index].rate) {
            values[index].total = Number(values[index].quantity) * Number(values[index].rate);
        }
        setOrderItems(values);
        calculateTotal(values);
    };
    const calculateTotal = (items) => {
        let total = 0;
        items.forEach((item) => {
            total += Number(item.total || 0);
        });
        setFormData({
            ...formData, total: total
        });
    };
    const addRow = () => {
        setOrderItems([...orderItems, {
            item: "", quantity: "", rate: "", total: ""
        }]);
    };
    const removeRow = (index) => {
        const values = [...orderItems];
        values.splice(index, 1);
        setOrderItems(values);
        calculateTotal(values);
    };
    const clearForm = () => {
        setEditId(null);
        setFormData({
            po_no: "",
            supplier: "",
            quotation: "",
            order_date: "",
            expected_date: "",
            total: "",
            status: "OPEN",
            remarks: ""
        });
        setOrderItems([{
            item: "", quantity: "", rate: "", total: ""
        }]);
    };
    const deleteData = (id) => {
        Swal.fire({
            title: "Delete Purchase Order?", icon: "warning", showCancelButton: true
        })
            .then((result) => {
                if (result.isConfirmed) {
                    axios.delete(baseUrl + `delete-purchase-order/${id}/`)
                        .then(() => {
                            Swal.fire("Deleted", "", "success");
                            getPurchaseOrders();
                        });
                }
            });
    };
    const saveData = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const data = new FormData();
            data.append("po_no", formData.po_no);
            data.append("supplier", formData.supplier);
            data.append("quotation", formData.quotation);
            data.append("order_date", formData.order_date);
            data.append("expected_date", formData.expected_date);
            data.append("total", formData.total);
            data.append("status", formData.status);
            data.append("remarks", formData.remarks);
            data.append("addedby", userName);
            data.append("ip_add", ipAddress);
            let purchaseOrderId = editId;
            if (editId === null) {
                const res = await axios.post(baseUrl + "purchase-order/", data);
                purchaseOrderId = res.data.id;
            } else {
                await axios.put(baseUrl + `update-purchase-order/${editId}/`, data);
            }
            for (const item of orderItems) {
                const itemData = new FormData();
                itemData.append("purchase_order", purchaseOrderId);
                itemData.append("item", item.item);
                itemData.append("quantity", item.quantity);
                itemData.append("rate", item.rate);
                itemData.append("total", item.total);
                if (item.id) {
                    await axios.put(baseUrl + `update-purchase-order-item/${item.id}/`, itemData);
                } else {
                    await axios.post(baseUrl + "purchase-order-item/", itemData);
                }
            }
            Swal.fire("Success", editId === null ? "Purchase Order Saved" : "Purchase Order Updated", "success");
            clearForm();
            getPurchaseOrders();
        } catch (error) {
            Swal.fire("Error", "Unable to Save Purchase Order", "error");
        }
        setLoading(false);
    };
    const editData = async (row) => {
        setEditId(row.id);
        setFormData({
            po_no: row.po_no,
            supplier: row.supplier.id,
            quotation: row.quotation?.id || "",
            order_date: row.order_date,
            expected_date: row.expected_date,
            total: row.total,
            status: row.status,
            remarks: row.remarks
        });
        const res = await axios.get(baseUrl + `purchase-order-by-item/${row.id}/`);
        setOrderItems(res.data.map(item => ({
            id: item.id, item: item.item.id, quantity: item.quantity, rate: item.rate, total: item.total
        })));
    };
    const columns = [{
        field: "po_no", headerName: "PO No", width: 160
    }, {
        field: "supplier", headerName: "Supplier", width: 220, renderCell: (params) => params.row.supplier?.name
    }, {
        field: "quotation",
        headerName: "Quotation",
        width: 170,
        renderCell: (params) => params.row.quotation?.quotation_no || ""
    }, {
        field: "order_date", headerName: "Order Date", width: 130
    }, {
        field: "expected_date", headerName: "Expected Date", width: 150
    }, {
        field: "total", headerName: "Total", width: 120
    }, {
        field: "status", headerName: "Status", width: 120
    }, {
        field: "action", headerName: "Action", width: 280, renderCell: (params) => (<>


                <button

                    className="btn btn-success btn-sm me-2"

                    onClick={() => editData(params.row)}

                >

                    Edit

                </button>


                <button

                    className="btn btn-danger btn-sm me-2"

                    onClick={() => deleteData(params.row.id)}

                >

                    Delete

                </button>
<button
                className="btn btn-primary btn-sm"
                onClick={() =>
                    window.open(
                        `/purchase-order-print/${params.row.id}`,
                        "_blank"
                    )
                }
            >
                Print
            </button>

            </>)
    }];
    return (<div className="container-fluid mt-3">


            <div className="card shadow">


                <div className="card-header bg-success text-white">


                    <h5 className="mb-0">

                        Purchase Order

                    </h5>


                </div>


                <div className="card-body">


                    <form onSubmit={saveData}>


                        <div className="row">


                            <div className="col-md-3 mb-3">


                                <label>

                                    PO No

                                </label>


                                <input


                                    type="text"


                                    className="form-control"


                                    name="po_no"


                                    value={formData.po_no}


                                    onChange={handleChange}


                                    required


                                />


                            </div>


                            <div className="col-md-3 mb-3">


                                <label>

                                    Supplier

                                </label>


                                <select


                                    className="form-control"


                                    name="supplier"


                                    value={formData.supplier}


                                    onChange={handleChange}


                                    required


                                >


                                    <option value="">


                                        Select Supplier


                                    </option>


                                    {supplierList.map((item) => (<option


                                            key={item.id}


                                            value={item.id}


                                        >


                                            {item.name || item.username}


                                        </option>))}


                                </select>


                            </div>

                            <div className="col-md-3 mb-3">


                                <label>

                                    Quotation

                                </label>


                                <select

                                    className="form-control"

                                    name="quotation"

                                    value={formData.quotation}

                                    onChange={handleChange}

                                >


                                    <option value="">

                                        Select Quotation

                                    </option>


                                    {quotationList.map((item) => (<option

                                            key={item.id}

                                            value={item.id}

                                        >

                                            {item.quotation_no}

                                        </option>))}


                                </select>


                            </div>


                            <div className="col-md-3 mb-3">


                                <label>

                                    Order Date

                                </label>


                                <input

                                    type="date"

                                    className="form-control"

                                    name="order_date"

                                    value={formData.order_date}

                                    onChange={handleChange}

                                    required

                                />


                            </div>


                            <div className="col-md-3 mb-3">


                                <label>

                                    Expected Date

                                </label>


                                <input

                                    type="date"

                                    className="form-control"

                                    name="expected_date"

                                    value={formData.expected_date}

                                    onChange={handleChange}

                                    required

                                />


                            </div>


                            <div className="col-md-3 mb-3">


                                <label>

                                    Status

                                </label>


                                <select

                                    className="form-control"

                                    name="status"

                                    value={formData.status}

                                    onChange={handleChange}

                                >


                                    <option value="OPEN">

                                        Open

                                    </option>


                                    <option value="PARTIAL">

                                        Partial

                                    </option>


                                    <option value="CLOSED">

                                        Closed

                                    </option>


                                </select>


                            </div>


                            <div className="col-md-12 mb-3">


                                <label>

                                    Remarks

                                </label>


                                <textarea

                                    className="form-control"

                                    rows="3"

                                    name="remarks"

                                    value={formData.remarks}

                                    onChange={handleChange}

                                />


                            </div>

                            <hr/>


                            <div className="col-md-12">


                                <div className="d-flex justify-content-between align-items-center mb-3">


                                    <h5>

                                        Purchase Order Items

                                    </h5>


                                    <button

                                        type="button"

                                        className="btn btn-primary"

                                        onClick={addRow}

                                    >

                                        + Add Item

                                    </button>


                                </div>


                                <div className="table-responsive">


                                    <table className="table-erp-modern">


                                        <thead className="table-success">


                                        <tr>


                                            <th width="35%">

                                                Item

                                            </th>


                                            <th width="15%">

                                                Quantity

                                            </th>


                                            <th width="15%">

                                                Rate

                                            </th>


                                            <th width="20%">

                                                Total

                                            </th>


                                            <th width="15%">

                                                Action

                                            </th>


                                        </tr>


                                        </thead>


                                        <tbody>


                                        {orderItems.map((row, index) => (<tr key={index}>


                                                <td>


                                                    <select


                                                        className="form-control"


                                                        name="item"


                                                        value={row.item}


                                                        onChange={(e) => handleItemChange(index, e)}


                                                        required


                                                    >


                                                        <option value="">


                                                            Select Item


                                                        </option>


                                                        {inventoryItems.map((item) => (<option


                                                                key={item.id}


                                                                value={item.id}


                                                            >


                                                                {item.item_name}


                                                            </option>))}


                                                    </select>


                                                </td>


                                                <td>


                                                    <input


                                                        type="number"


                                                        step="0.01"


                                                        className="form-control"


                                                        name="quantity"


                                                        value={row.quantity}


                                                        onChange={(e) => handleItemChange(index, e)}


                                                        required


                                                    />


                                                </td>


                                                <td>


                                                    <input


                                                        type="number"


                                                        step="0.01"


                                                        className="form-control"


                                                        name="rate"


                                                        value={row.rate}


                                                        onChange={(e) => handleItemChange(index, e)}


                                                        required


                                                    />


                                                </td>


                                                <td>


                                                    <input


                                                        type="number"


                                                        className="form-control"


                                                        value={row.total}


                                                        readOnly


                                                    />


                                                </td>


                                                <td>


                                                    {orderItems.length > 1 && (<button


                                                            type="button"


                                                            className="btn btn-danger btn-sm"


                                                            onClick={() => removeRow(index)}


                                                        >


                                                            Remove


                                                        </button>)}


                                                </td>


                                            </tr>))}


                                        </tbody>


                                    </table>


                                </div>


                            </div>


                            <div className="col-md-3 mb-3">


                                <label>


                                    Grand Total


                                </label>


                                <input


                                    type="number"


                                    className="form-control"


                                    value={formData.total}


                                    readOnly


                                />


                            </div>


                            <div className="col-md-12 mt-3">


                                <button


                                    type="submit"


                                    className="btn btn-success me-2"


                                    disabled={loading}


                                >


                                    {editId === null ? "Save Purchase Order" : "Update Purchase Order"}


                                </button>


                                <button


                                    type="button"


                                    className="btn btn-secondary"


                                    onClick={clearForm}


                                >


                                    Clear


                                </button>


                            </div>


                        </div>


                    </form>


                </div>


            </div>

            <div className="card shadow mt-3">


                <div className="card-header bg-primary text-white">


                    <h5 className="mb-0">

                        Purchase Order List

                    </h5>


                </div>


                <div className="card-body">


                    <div

                        style={{
                            height: 600, width: "100%"
                        }}

                    >


                        <ERPDataGrid


                            rows={rows}


                            columns={columns}


                            loading={loading}


                            getRowId={(row) => row.id}


                            pageSizeOptions={[10, 25, 50, 100]}


                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        pageSize: 10
                                    }
                                }
                            }}


                            disableRowSelectionOnClick


                        />


                    </div>


                </div>


            </div>


        </div>);
}
export default PurchaseOrder;